import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Webhook, ShieldCheck, Code2, ChevronRight, ArrowLeft,
} from "lucide-react";

/**
 * Xchangely Pay developer docs — single long-form page covering the
 * webhook signing contract + payload taxonomy + sandbox tips. Linked from
 * the merchant welcome email and the marketing landing page.
 *
 * Intentionally NOT a full API reference (we generate that from the OpenAPI
 * spec in prod). This page is the "how do I integrate?" walk-through.
 */
export default function BusinessDocs() {
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  return (
    <div className="min-h-screen bg-background" data-testid="business-docs">
      {/* Top bar */}
      <header className="bg-foreground text-white sticky top-0 z-30 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/business" className="font-display font-bold text-lg flex items-center gap-2"
                data-testid="docs-logo-link">
            <span className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-emerald-300" />
            </span>
            <span>Xchangely <span className="text-emerald-300">Pay</span> · Docs</span>
          </Link>
          <Link to="/pay"
                className="text-xs px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-foreground font-semibold transition-colors"
                data-testid="docs-portal-link">
            Open portal
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10 grid md:grid-cols-[200px_1fr] gap-8">
        {/* TOC */}
        <aside className="hidden md:block sticky top-20 self-start" data-testid="docs-toc">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Contents</div>
          <nav className="space-y-1.5 text-sm">
            <a href="#quickstart" className="block text-foreground hover:text-emerald-700">Quickstart</a>
            <a href="#webhooks" className="block text-foreground hover:text-emerald-700">Webhooks</a>
            <a href="#verify" className="block text-foreground hover:text-emerald-700">Signature verification</a>
            <a href="#events" className="block text-foreground hover:text-emerald-700">Event taxonomy</a>
            <a href="#testing" className="block text-foreground hover:text-emerald-700">Testing</a>
            <a href="#support" className="block text-foreground hover:text-emerald-700">Support</a>
          </nav>
          <Link to="/business" className="inline-flex items-center gap-1 mt-6 text-xs text-muted-foreground hover:text-foreground"
                data-testid="docs-back-link">
            <ArrowLeft className="w-3 h-3" /> Back to marketing
          </Link>
        </aside>

        {/* Body */}
        <div className="prose-content space-y-10">
          {/* Quickstart */}
          <section id="quickstart" data-testid="docs-quickstart">
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-emerald-700 font-bold">
              <Code2 className="w-3.5 h-3.5" /> Quickstart
            </span>
            <h1 className="font-display font-bold text-3xl sm:text-4xl mt-3 mb-3">
              Take your first payment in 5 minutes.
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              Xchangely Pay is API-light by design — most merchants go live by simply
              generating links from the portal and watching status update on screen.
              You only need to integrate webhooks if you want your own backend to
              know the moment a customer pays.
            </p>
            <ol className="mt-4 list-decimal pl-5 space-y-2 text-sm">
              <li>Sign in to <Link to="/pay" className="text-emerald-700 font-semibold underline">your merchant portal</Link>.</li>
              <li>Open the <strong>Webhooks &amp; API</strong> panel (just below the create-link form).</li>
              <li>Paste your endpoint URL — anything HTTPS that accepts JSON POST.</li>
              <li>Copy the <code className="bg-muted px-1 rounded">whsec_…</code> secret into your server&apos;s env vars.</li>
              <li>Mint a test link, pay it from another tab, and watch your endpoint fire.</li>
            </ol>
          </section>

          {/* Webhooks */}
          <section id="webhooks" data-testid="docs-webhooks">
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-emerald-700 font-bold">
              <Webhook className="w-3.5 h-3.5" /> Webhooks
            </span>
            <h2 className="font-display font-bold text-2xl mt-2 mb-2">When we call you</h2>
            <p className="text-muted-foreground mb-3">
              We POST to your configured URL on every <strong>terminal</strong> status change
              of a payment link. Non-terminal transitions (e.g. <code className="bg-muted px-1 rounded">initiated</code>
              → <code className="bg-muted px-1 rounded">pending</code>) are NOT delivered to keep your
              endpoint quiet. We retry up to <strong>3 times</strong> with exponential back-off (1.5s, 3s)
              if you respond with anything outside <code className="bg-muted px-1 rounded">2xx</code>.
            </p>
            <div className="xch-card p-4 mt-3">
              <div className="text-[11px] uppercase font-bold text-muted-foreground mb-2">Sample payload</div>
              <pre className="text-[11px] overflow-x-auto bg-muted/40 p-3 rounded-lg leading-relaxed">{`{
  "event": "link.completed",
  "link_id": "lnk_…",
  "session_id": "ps_…",
  "status": "completed",
  "amount": 24.99,
  "from_currency": "EUR",
  "to_currency": "USDC",
  "method": "card",
  "reference": "INV-2026-0142",
  "customer_email": "alice@buyer.co",
  "created_at": "2026-06-15T18:24:01+00:00",
  "updated_at": "2026-06-15T18:31:48+00:00"
}`}</pre>
              <div className="text-[11px] uppercase font-bold text-muted-foreground mt-4 mb-2">Headers</div>
              <pre className="text-[11px] overflow-x-auto bg-muted/40 p-3 rounded-lg leading-relaxed">{`Content-Type:                application/json
User-Agent:                  Xchangely-Pay-Webhook/1.0
X-Xchangely-Pay-Event:       link.completed
X-Xchangely-Pay-Timestamp:   1781546925
X-Xchangely-Pay-Signature:   sha256=abc123…
X-Xchangely-Pay-Merchant:    <your-merchant-id>`}</pre>
            </div>
          </section>

          {/* Verify */}
          <section id="verify" data-testid="docs-verify">
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-emerald-700 font-bold">
              <ShieldCheck className="w-3.5 h-3.5" /> Signature verification
            </span>
            <h2 className="font-display font-bold text-2xl mt-2 mb-2">Verify before you trust</h2>
            <p className="text-muted-foreground mb-3">
              We sign every delivery with <strong>HMAC-SHA256</strong>. Compute the
              expected signature on your side and compare with the
              <code className="bg-muted px-1 rounded mx-1">X-Xchangely-Pay-Signature</code> header
              using a <strong>constant-time</strong> compare. Replay protection: reject
              deliveries whose <code className="bg-muted px-1 rounded">Timestamp</code> header
              is older than ~5 minutes.
            </p>
            <div className="xch-card p-4">
              <div className="text-[11px] uppercase font-bold text-muted-foreground mb-2">Node.js (Express)</div>
              <pre className="text-[11px] overflow-x-auto bg-muted/40 p-3 rounded-lg leading-relaxed">{`import crypto from "crypto";

app.post("/webhooks/xchangely", express.raw({type: "application/json"}), (req, res) => {
  const ts  = req.header("X-Xchangely-Pay-Timestamp");
  const sig = (req.header("X-Xchangely-Pay-Signature") || "").replace(/^sha256=/, "");
  const expected = crypto.createHmac("sha256", process.env.XCHANGELY_WEBHOOK_SECRET)
    .update(\`t=\${ts}.\` + req.body)
    .digest("hex");
  if (!crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(sig))) {
    return res.status(401).end();
  }
  // Optional: reject stale deliveries
  if (Math.abs(Date.now()/1000 - Number(ts)) > 300) return res.status(400).end();
  const body = JSON.parse(req.body.toString());
  // … your handler …
  res.json({ ok: true });
});`}</pre>
              <div className="text-[11px] uppercase font-bold text-muted-foreground mt-4 mb-2">Python (Flask)</div>
              <pre className="text-[11px] overflow-x-auto bg-muted/40 p-3 rounded-lg leading-relaxed">{`import hmac, hashlib, os, time
from flask import request, abort

@app.post("/webhooks/xchangely")
def xchangely_webhook():
    ts  = request.headers.get("X-Xchangely-Pay-Timestamp", "")
    sig = request.headers.get("X-Xchangely-Pay-Signature", "").removeprefix("sha256=")
    body = request.get_data()  # raw bytes, before JSON parsing
    expected = hmac.new(
        os.environ["XCHANGELY_WEBHOOK_SECRET"].encode(),
        f"t={ts}.".encode() + body, hashlib.sha256,
    ).hexdigest()
    if not hmac.compare_digest(expected, sig):
        abort(401)
    if abs(time.time() - int(ts)) > 300:
        abort(400)
    payload = request.get_json()
    # … your handler …
    return {"ok": True}`}</pre>
            </div>
          </section>

          {/* Events */}
          <section id="events" data-testid="docs-events">
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-emerald-700 font-bold">
              <ChevronRight className="w-3.5 h-3.5" /> Event taxonomy
            </span>
            <h2 className="font-display font-bold text-2xl mt-2 mb-3">What we send</h2>
            <div className="xch-card divide-y divide-border" data-testid="docs-events-table">
              {[
                ["link.completed", "Customer paid; merchant payout queue updated."],
                ["link.failed",    "Card declined / payment timed out / provider rejected."],
                ["link.cancelled", "Merchant cancelled the link via the portal."],
                ["link.refunded",  "Settlement was refunded to the customer."],
              ].map(([ev, body]) => (
                <div key={ev} className="px-4 py-3 grid md:grid-cols-[200px_1fr] gap-2 text-sm">
                  <code className="font-mono text-emerald-700 font-semibold">{ev}</code>
                  <span className="text-muted-foreground">{body}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Idempotency: treat <code className="bg-muted px-1 rounded">link_id</code> +
              <code className="bg-muted px-1 rounded mx-1">event</code> as a deduplication key.
              We can retry deliveries up to 3 times per status transition, and replays
              from the admin tool will arrive as additional POSTs.
            </p>
          </section>

          {/* Testing */}
          <section id="testing" data-testid="docs-testing">
            <h2 className="font-display font-bold text-2xl mb-2">Testing</h2>
            <p className="text-muted-foreground mb-2">
              Need to test without spending real cents? You have two options:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-sm">
              <li>Use a tool like <code className="bg-muted px-1 rounded">ngrok</code> /
                <code className="bg-muted px-1 rounded mx-1">smee.io</code> to expose your local server.</li>
              <li>Ask staff to manually fire a test webhook to your URL from the
                admin replay tool — they can replay any past delivery against
                your current secret + URL.</li>
            </ul>
          </section>

          {/* Support */}
          <section id="support" data-testid="docs-support">
            <h2 className="font-display font-bold text-2xl mb-2">Support</h2>
            <p className="text-muted-foreground">
              Webhook misbehaving? Email{" "}
              <a href="mailto:business@xchangely.io" className="text-emerald-700 underline">business@xchangely.io</a> with
              the failing <code className="bg-muted px-1 rounded">link_id</code> and the
              <code className="bg-muted px-1 rounded mx-1">X-Xchangely-Pay-Timestamp</code> header
              from a recent failure — we&apos;ll pull the matching row from our delivery
              audit and tell you exactly what we sent and how your endpoint responded.
            </p>
          </section>
        </div>
      </main>

      <footer className="py-7 bg-foreground text-white border-t border-white/5 text-xs text-white/40">
        <div className="max-w-5xl mx-auto px-5 flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} Xchangely Pay</div>
          <div className="flex gap-4">
            <Link to="/business" className="hover:text-white/70" data-testid="docs-footer-marketing">Marketing</Link>
            <Link to="/pay" className="hover:text-white/70" data-testid="docs-footer-portal">Portal</Link>
            <Link to="/legal/terms" className="hover:text-white/70" data-testid="docs-footer-terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
